import { cloneElement, isValidElement, useEffect, useMemo, useState, type ReactElement } from 'react';
import { Sparkles } from 'lucide-react';
import AiSampleSize from './ai-sample-size';
import { isQA } from 'src/helpers/env.helpers';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  PortalDsButton,
  PortalDsEmptyState,
  PortalDsField,
  PortalDsInput,
  PortalDsSelect,
  portalDs,
} from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import {
  SurveyAudiencePanelField,
  SurveyAudienceWithPanelFields,
  useGetSurveyAudiencesWithPanelFieldsQuery,
  usePreviewSurveyAudienceMutation,
} from 'src/redux/survey/survey.endpoints';

interface CriteriaCardProps {
  extSurveyId: string;
  trigger?: ReactElement<{ onClick?: () => void }>;
}

function getFieldKey(field: SurveyAudiencePanelField): string {
  return field.name || field.id;
}

function getSelectedPanelId(audience: SurveyAudienceWithPanelFields | null): string {
  if (!audience) {
    return '';
  }
  return audience.id ?? audience.panel_fields?.[0]?.panel ?? '';
}

const SurveyResponderTrigger = ({ extSurveyId, trigger }: CriteriaCardProps) => {
  const { data: audiences } = useGetSurveyAudiencesWithPanelFieldsQuery(null);
  const [selectedAudience, setSelectedAudience] = useState<SurveyAudienceWithPanelFields | null>(null);
  const [fieldSelections, setFieldSelections] = useState<Record<string, string[]>>({});
  const [fieldSearchTerms, setFieldSearchTerms] = useState<Record<string, string>>({});
  const [isOpen, setIsOpen] = useState(false);
  const sampleSizes = isQA() ? [0, 10, 50, 100, 200] : [0, 100, 200, 500, 750, 1000];

  const aiAudiences =
    audiences?.filter(
      (audience) => audience.panel_type === 'ai' && audience.status === 'active' && audience.locked === true
    ) ?? [];

  const toggleMultiValue = (field: SurveyAudiencePanelField, value: string) => {
    const key = getFieldKey(field);
    setFieldSelections((prev) => {
      const current = prev[key] ?? [];
      const next = current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
      return { ...prev, [key]: next };
    });
  };

  const setSingleValue = (field: SurveyAudiencePanelField, value: string) => {
    const key = getFieldKey(field);
    setFieldSelections((prev) => ({ ...prev, [key]: [value] }));
  };

  const handleFieldSearchChange = (fieldKey: string, value: string) => {
    setFieldSearchTerms((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const handleAudienceChange = (audienceId: string) => {
    const next = aiAudiences.find((audience) => audience.id === audienceId) ?? null;
    setSelectedAudience(next);
    setFieldSelections({});
    setFieldSearchTerms({});
  };

  const audienceCriteria = useMemo(() => {
    if (!selectedAudience) {
      return {};
    }

    const allowedFieldKeys = new Set(
      selectedAudience.panel_fields.map((field) => getFieldKey(field))
    );

    return Object.fromEntries(
      Object.entries(fieldSelections).filter(
        ([key, values]) => allowedFieldKeys.has(key) && values.length > 0
      )
    );
  }, [selectedAudience, fieldSelections]);

  const selectedPanelId = useMemo(() => getSelectedPanelId(selectedAudience), [selectedAudience]);

  const fieldDisplayNameByKey = useMemo(() => {
    if (!selectedAudience) {
      return {};
    }

    return Object.fromEntries(
      selectedAudience.panel_fields.map((field) => [
        getFieldKey(field),
        field.display_name || field.name,
      ])
    );
  }, [selectedAudience]);

  const [
    previewAudience,
    {
      data: audiencePreview,
      isLoading: isPreviewLoading,
    },
  ] = usePreviewSurveyAudienceMutation();

  useEffect(() => {
    if (!selectedAudience || !selectedPanelId) {
      return;
    }

    const timeout = setTimeout(() => {
      previewAudience({
        panel: selectedPanelId,
        criteria: audienceCriteria as Record<string, string[]>,
      });
    }, 400);

    return () => clearTimeout(timeout);
  }, [selectedAudience, selectedPanelId, audienceCriteria, previewAudience]);

  const selectedCriteriaSummary = useMemo(() => {
    const entries = Object.entries(audienceCriteria);
    if (!entries.length) {
      return '';
    }

    const formatValues = (values: string[]) => {
      const first = values.slice(0, 3);
      const remaining = values.length - first.length;
      if (remaining <= 0) {
        return first.join(', ');
      }
      return `${first.join(', ')} +${remaining} more`;
    };

    return entries
      .map(([key, values]) => {
        const label = fieldDisplayNameByKey[key] ?? key;
        return `${label}: ${formatValues(values)}`;
      })
      .join(' · ');
  }, [audienceCriteria, fieldDisplayNameByKey]);

  const estimatedPanelistPercent = useMemo(() => {
    if (!audiencePreview) {
      return null;
    }
    const previewCount = Number(audiencePreview.preview_count ?? 0);
    const totalPanelists = Number(audiencePreview.total_panelists ?? 0);
    if (!totalPanelists) {
      return null;
    }
    return Math.round((previewCount / totalPanelists) * 100);
  }, [audiencePreview]);

  const openDialog = () => {
    if (!extSurveyId) {
      return;
    }
    setIsOpen(true);
  };

  const activeFields = selectedAudience?.panel_fields.filter(
    (field) => field.status === 'active' || field.status === undefined
  ) ?? [];

  return (
    <div>
      {trigger && isValidElement(trigger) ? (
        cloneElement(trigger, { onClick: openDialog })
      ) : (
        <PortalDsButton
          size="sm"
          variant="accent"
          aria-label="AI panelists"
          title="Add AI panelists"
          onClick={openDialog}
        >
          <Sparkles className="size-3.5" />
          AI
        </PortalDsButton>
      )}

      <Dialog
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden rounded-xl border border-[#EDEEF2] bg-white p-0 shadow-[0_8px_24px_rgba(13,15,26,0.12)] sm:max-w-xl">
          <DialogHeader className="shrink-0 border-b border-[#EDEEF2] px-6 py-5 pr-12 text-left">
            <DialogTitle className={portalDs.sectionTitle}>
              AI panelists
            </DialogTitle>
            <DialogDescription className={portalDs.sectionSubtitle}>
              Add simulated respondents from an AI audience to this survey.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
            {aiAudiences.length === 0 ? (
              <PortalDsEmptyState
                title="No AI audiences available"
                description="Active, locked AI audiences will appear here when they are set up for this workspace."
              />
            ) : (
              <PortalDsField
                label="Audience"
                htmlFor="ai-panel-audience"
                filter
              >
                <PortalDsSelect
                  id="ai-panel-audience"
                  value={selectedAudience?.id ?? ''}
                  onChange={(event) => handleAudienceChange(event.target.value)}
                >
                  <option value="">
                    Select audience
                  </option>
                  {aiAudiences.map((audience) => (
                    <option
                      key={audience.id}
                      value={audience.id}
                    >
                      {audience.name}
                    </option>
                  ))}
                </PortalDsSelect>
              </PortalDsField>
            )}

            {selectedAudience && activeFields.length > 0 ? (
              <div className={portalDs.formPanel}>
                <p className={cn(portalDs.filterLabel, 'mb-3')}>
                  Panel filters
                </p>
                <div className="max-h-[230px] space-y-2 overflow-y-auto pr-1">
                  {activeFields.map((field) => {
                    const control = field.selector_configuration?.control;
                    const options = field.selector_configuration?.options ?? [];
                    const isMulti = (control?.selection ?? 'single') === 'multi';
                    const fieldKey = getFieldKey(field);
                    const selectedValues = fieldSelections[fieldKey] ?? [];
                    const searchValue = fieldSearchTerms[fieldKey] ?? '';
                    const normalizedQuery = searchValue.trim().toLowerCase();
                    const shouldShowSearch = options.length > 10;
                    const filteredOptions = !shouldShowSearch || !normalizedQuery
                      ? options
                      : options.filter((option) => {
                        const label = option.label ?? '';
                        const value = option.value ?? '';
                        return (
                          label.toLowerCase().includes(normalizedQuery)
                          || value.toLowerCase().includes(normalizedQuery)
                        );
                      });

                    if (!options.length) {
                      return null;
                    }

                    return (
                      <details
                        key={field.id}
                        className="rounded-lg border border-[#EDEEF2] bg-white p-3"
                      >
                        <summary className="flex cursor-pointer list-none items-center justify-between text-left">
                          <span className={cn(portalDs.bodySm, 'font-semibold text-[#0D0F1A]')}>
                            {field.display_name || field.name}
                          </span>
                          <span className={portalDs.caption}>
                            {selectedValues.length ? selectedValues.length : 'None'}
                          </span>
                        </summary>
                        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-2">
                          {shouldShowSearch ? (
                            <PortalDsInput
                              value={searchValue}
                              onChange={(event) => handleFieldSearchChange(fieldKey, event.target.value)}
                              placeholder="Search options…"
                            />
                          ) : null}
                          {filteredOptions.length === 0 ? (
                            <p className={portalDs.caption}>
                              No matching options
                            </p>
                          ) : filteredOptions.map((option) => {
                            const checked = selectedValues.includes(option.value);
                            return (
                              <label
                                key={option.value}
                                className={cn(portalDs.bodySm, 'flex items-center gap-2 text-[#0D0F1A]')}
                              >
                                <input
                                  type={isMulti ? 'checkbox' : 'radio'}
                                  name={isMulti ? undefined : `radio_${field.id}`}
                                  checked={checked}
                                  onChange={() => (
                                    isMulti
                                      ? toggleMultiValue(field, option.value)
                                      : setSingleValue(field, option.value)
                                  )}
                                />
                                <span>
                                  {option.label}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </details>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {selectedAudience && selectedPanelId ? (
              <div className={cn(portalDs.alertBase, portalDs.alertInfo)}>
                <div>
                  <div className={portalDs.alertTitle}>
                    Estimated panelists
                  </div>
                  <div className={cn(portalDs.alertBody, 'mt-1')}>
                    {selectedCriteriaSummary
                      ? `Selected: ${selectedCriteriaSummary}`
                      : 'No filters selected'}
                  </div>
                  <div className="mt-2 text-[13.5px] font-semibold text-[#0D0F1A]">
                    {isPreviewLoading
                      ? 'Estimating…'
                      : audiencePreview
                        ? `${Number(audiencePreview.preview_count ?? 0).toLocaleString()} / ${Number(audiencePreview.total_panelists ?? 0).toLocaleString()}${estimatedPanelistPercent != null ? ` (~${estimatedPanelistPercent}%)` : ''}`
                        : '—'}
                  </div>
                </div>
              </div>
            ) : null}

            {!selectedAudience && aiAudiences.length > 0 ? (
              <p className={portalDs.bodySm}>
                Select an audience to choose a sample size and add AI panelists.
              </p>
            ) : null}
          </div>

          {selectedAudience ? (
            <DialogFooter className="shrink-0 border-t border-[#EDEEF2] px-6 py-4 sm:flex-col sm:space-x-0">
              <AiSampleSize
                extSurveyId={extSurveyId}
                sampleSizes={sampleSizes}
                panelId={selectedPanelId}
                audienceCriteria={audienceCriteria}
                zeroDisabled={false}
                onCancel={() => setIsOpen(false)}
                onSubmit={() => setIsOpen(false)}
              />
            </DialogFooter>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SurveyResponderTrigger;
